import { useCallback, useRef, useState } from "react";
import { DesktopViewer } from "./viewer";
export function useDesktopViewer(options) {
    const containerRef = useRef(null);
    const viewerRef = useRef(null);
    const [state, setState] = useState("disconnected");
    const [errorMessage, setErrorMessage] = useState(null);
    // Stash the latest options in a ref so the viewer's long-lived
    // closures (refresh callback, error handler) always see the freshest
    // values without us having to re-create the viewer when a parent
    // component re-renders. React patterns 101.
    const optsRef = useRef(options);
    optsRef.current = options;
    const disconnect = useCallback(() => {
        viewerRef.current?.disconnect();
        viewerRef.current = null;
        setState("disconnected");
        setErrorMessage(null);
    }, []);
    const connect = useCallback(() => {
        const params = optsRef.current.streamParams;
        if (!params || !containerRef.current)
            return;
        disconnect();
        setErrorMessage(null);
        const viewer = new DesktopViewer({
            streamParams: params,
            container: containerRef.current,
            // Each callback reads from `optsRef.current` so a re-rendered
            // parent that supplies a different handler picks up immediately.
            onConnected: () => {
                setState("connected");
                setErrorMessage(null);
                optsRef.current.onConnected?.();
            },
            onDisconnected: () => {
                setState("disconnected");
                optsRef.current.onDisconnected?.();
            },
            onError: (err) => {
                setState("failed");
                setErrorMessage(err.message);
                optsRef.current.onError?.(err);
            },
            // Wrap in a stable closure that re-reads optsRef so the caller
            // can swap in a different refresher without us tearing the
            // viewer down. Returns the result of the latest function so
            // viewer's promise chain works regardless of caller stability.
            refreshStreamParams: optsRef.current.refreshStreamParams
                ? () => {
                    const fn = optsRef.current.refreshStreamParams;
                    if (!fn) {
                        return Promise.reject(new Error("refreshStreamParams unwired mid-session"));
                    }
                    return fn();
                }
                : undefined,
            refreshLeadMs: optsRef.current.refreshLeadMs,
        });
        viewerRef.current = viewer;
        setState("connecting");
        viewer.connect();
    }, [disconnect]);
    const retry = useCallback(() => {
        if (viewerRef.current) {
            // Existing viewer: route through its retry() which refreshes
            // creds first, then disconnects + reconnects internally. We
            // still surface the connecting state to the UI so overlays
            // animate.
            setState("connecting");
            setErrorMessage(null);
            void viewerRef.current.retry();
            return;
        }
        // No live viewer (we're sitting in disconnected state) → just
        // start a fresh connect, which will pull the latest streamParams
        // off optsRef.current.
        connect();
    }, [connect]);
    // Cleanup is handled by the caller via disconnect().
    // Not using useEffect cleanup here because React StrictMode
    // double-mounts would kill an in-progress MQTT connection.
    return { containerRef, state, errorMessage, connect, disconnect, retry };
}
//# sourceMappingURL=use-desktop-viewer.js.map